import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { TSelectboxOption } from "../../components/admin/formFields";
import { APP_CONFIG, REPORT_TYPES } from "../../constants/constants";
import { TLatLng, TMapState, TMapType } from "../../types/map";
import { LegacyOrganizationAccount } from "../types/Account";
import {
  LegacyOrganizationAlertListingPayload,
  SingleOrganizationVehicleAlertPayload,
} from "../types/Alert";
import {
  CameraDeviceConfigProfile,
  CameraWakeupRequiredInfo,
  DeviceEventsListPayload,
  FileRetrievalListingPayload,
} from "../types/Camera";
import { VMDashNotifyResponseIncomingCall } from "../types/CamerasVisionMax";
import { CrashTrackListingPayload } from "../types/CrashTrack";
import { LegacyDocumentEntityListingPayload } from "../types/Document";
import { LegacyDriverEntityListingPayload } from "../types/Driver";
import { LegacyFleettagEntityListingPayload } from "../types/Fleettag";
import { LegacyGeozoneEntityListingPayload } from "../types/Geozone";
import { LegacyGeozoneCategoryEntityListingPayload } from "../types/GeozoneCategory";
import { GlobalSearchPayload } from "../types/GlobalSearch";
import { LegacyGroupEntityListingPayload, OrgGHGroup } from "../types/Group";
import { LegacyMaintenanceEntityListingPayload } from "../types/Maintenance";
import {
  BuzzerAlertListingPayload,
  BuzzerAlertWithUUID,
} from "../types/Notifications";
import { LegacyVerifyEmailOtpResponseSuccess } from "../types/Onboarding";
import {
  CustomWidget,
  DASHBOARD_WIDGET_TYPE,
  DashboardWidget,
} from "../types/Overview";
import { StandardReportPayload, TReport, TReportStatus } from "../types/Report";
import { LegacyRuleEntityListingPayload } from "../types/Rule";
import { EnterpriseListingPayload } from "../types/Sysadmin";
import { LegacyUserEntityListingPayload } from "../types/User";
import {
  LegacyRoleEntityListingPayload,
  OrganizationRoleACLPayload,
} from "../types/UserRole";
import { LegacyVehicleEntityListingPayload } from "../types/Vehicle";
import { VehicleMapDataPoint } from "../types/VehicleMap";
import { WhiteLabelParam } from "../types/WhiteLabelConfig";
import { IncidentListingPayload } from "../../pages/admins/incident/type";

export type TPreLoginUserData = {
  email?: string;
  sso?: boolean;
  clientid?: string;
  tenantid?: string;
};

export type TViewedBuzzerAlert = {
  uuid: string;
  viewedAt: number;
};

export type TModalsState = {
  showAdminsDropdown: boolean;
  showEditColumnsDashboard: boolean;
  showEditColumns: boolean;
  showCreateUser: boolean;
  showCreateRole: boolean;
  showCloneRole: boolean;
  showCreateVehicle: boolean;
  showCreateDriver: boolean;

  showCreateGroup: boolean;
  createGroupUnder: string; // (group hierarchy) used to store the parent group id when creating a new group
  showConfirmMoveGroup: boolean;
  showEditGroup: boolean;
  editGroupId: string;
  editGroupNewMode: boolean;
  showHierarchyGroupSelector: boolean;
  showReadOnlyViewForHierarchyGroup: OrgGHGroup | null;

  showBuzzerAlertSingle: boolean;
  showBuzzerAlertList: boolean;
  buzzerAlerts: BuzzerAlertWithUUID[]; // used to store the buzzer alerts to be shown on the popup
  buzzerAlertsViewed: TViewedBuzzerAlert[]; // used to store the buzzer alerts that have been viewed
  buzzerAlertsInProgress: TViewedBuzzerAlert[]; // used to store the buzzer alerts that are in progress

  showCreateCustomIcon?: boolean;
  showCreateFleetTag: boolean;
  showCreateGeozone: boolean;
  showCreateGeozoneCategory: boolean;
  showDeleteConfirmation: boolean;
  showDeleteImageConfirmation: boolean;
  showResetConfirmation: boolean;
  showMapFilter: boolean;
  showGroupSelector: boolean;
  showVehicleDetails: boolean;
  showVehicleLiveCameras: boolean;
  showVehicleFilter: boolean;
  showEventFilter: boolean;
  showLayerFilter: boolean;
  showWeatherForecast: boolean;
  showDuplicateReportModal: boolean;
  showInternalShareReportModal: boolean;
  showShareViaEmailReportModal: boolean;
  showAddEditReportModal: boolean;
  showSendText: boolean;
  showSendLocation: boolean;
  showReportOptions: boolean;
  showCustomReportOptions: boolean;
  showUpdateReportOptions: boolean;
  showReportTypeSelector: boolean;
  showVehicleCameraVideo: boolean;
  showCreateRule: boolean;
  isExpandedSideMenu: boolean;
  showAddMaintenanceDetails: boolean;
  showNotifications: boolean;
  showAlertNotifications: boolean;
  showNotificationPrefs: boolean;
  maintBucket: string;
  maintName: string;
  showUploadDocumentModal: boolean;
  showEditDocumentModal: boolean;
  showAddUsersToRoleModal: boolean;
  showCreateGroupChatModal: boolean;
  showCreateGroupSMSModal: boolean;
  showCreateDashboard: boolean;
  editDashboardId: number | null;
  showDashboardSelectorDropdown: boolean;
  showDashboardAddWidget: boolean;
  showGlobalSearchDropdown: boolean;

  showSysadminEnterprisesCreateNew: boolean;

  showPrimaryOverlay: boolean;

  showSwitchChartType: boolean;
  switchChartTypeForWidget: DASHBOARD_WIDGET_TYPE | null; // used to store the widget type for which the chart type is to be switched
  switchChartTypeForCustomWidgetId: number | null; // used to store the custom widget id for which the chart type is to be switched

  widgetSelectedForPrint: DashboardWidget | null;
  showModalNewCustomReportStep1: boolean;
  showModalNewCustomReportStep2: boolean;
  showModalNewCustomReportStep3: boolean;
  showModalMakeCustomReport: boolean;
  showAdvanceFilterPopup: boolean;

  showModalMakeCopy: boolean;
  showUseCustomReport: boolean;
  customWidgetReport: TReport | null;
  customWidgetToEdit: CustomWidget | null;
  showModalAssetDecodeVIN: boolean;
  showModalSelectDelegatedAccessUser: boolean;
  showModalMaintenanceFaultCodes: boolean;

  // cameras admin - visionmax
  showModalLinkAsset: boolean;
  showModalVMCameraLiveviewSN: string | null;
  showModalVMCameraLiveviewContinueSN: string | null;
  showModalVMCameraVoicecallSN: string | null;
  voiceCallAssetId?: string | null;
  voiceCallDriverName?: string | null;
  showModalVMCameraVoicecallIncoming: VMDashNotifyResponseIncomingCall | null;
  showParkingPhotoView: boolean;
  showModalFormatSDCard: boolean;
  showModalVMICChistory: boolean;
  showModalDiagnoseCamera: boolean;
  showModalWakeupCamera: CameraWakeupRequiredInfo | null;
  showGroupCameraSelector: boolean;
  showSnapShotsView: boolean;
  showEventDownloadConfirmation: boolean;
  showEventComments: boolean;
  showPreviousSaveConfirmation: boolean;
  showRestoreDefaultConfirmation: boolean;
  showModalDeleteFile: boolean;
  showRequestVideo: {
    show: boolean;
    timestamp: number;
  };
  showModalVideoPlayback: {
    startTimestamp: number;
    duration: number;
    captureMode: string;
    storageType?: string; // added storageType to indicate the type of storage (SDCARD or SDCARD_2)
    action: "play" | "pause" | "stop"; // added action to indicate playback state
  } | null;

  showContextMenu: boolean;
  contextMenuOptions: {
    Content: React.ReactNode;
    AnchorRef?: any; // should be MutableRefObject<HTMLElement>
    relativePosition: "top-left" | "top-right" | "bottom-left" | "bottom-right";
    widthClass: string;
  };

  // White Labeling
  showReferenceLandingModal: boolean;
  showDeleteDomainModal: boolean;
};
const ownerOrganization = localStorage.getItem("ownerOrganization");

export type TListingQueryParams = {
  // when adding new items here, please make sure to add them to the following places:
  // 1. /src/components/dashboard/dashboard.tsx
  // 2. /src/hooks/loginHandler.ts
  users: LegacyUserEntityListingPayload;
  roles: LegacyRoleEntityListingPayload;
  vehicles: LegacyVehicleEntityListingPayload;
  drivers: LegacyDriverEntityListingPayload;
  groups: LegacyGroupEntityListingPayload;
  fleetTags: LegacyFleettagEntityListingPayload;
  geoZones: LegacyGeozoneEntityListingPayload;
  geozoneCategories: LegacyGeozoneCategoryEntityListingPayload;
  vehicleAlerts: LegacyOrganizationAlertListingPayload;
  vehicleAlertsSingle: SingleOrganizationVehicleAlertPayload;
  buzzerAlerts: BuzzerAlertListingPayload;
  reports: StandardReportPayload;
  incidentsReview: IncidentListingPayload;
  rules: LegacyRuleEntityListingPayload;
  maintenance: LegacyMaintenanceEntityListingPayload;
  documents: LegacyDocumentEntityListingPayload;
  roleCategoryACL: OrganizationRoleACLPayload;
  globalSearch: GlobalSearchPayload;

  sysadminEnterprises: EnterpriseListingPayload;
  accountUsers: LegacyUserEntityListingPayload;

  // camera admin (visionmax)
  deviceEvents: DeviceEventsListPayload & {
    driver_name?: string;
    duration?: string | number;
  };
  deviceEventsDashboard: DeviceEventsListPayload & {
    driver_name?: string;
    duration?: string | number;
  };
  deviceEventsSelectedCameraOption: TSelectboxOption | null;
  deviceFileSelectedCameraOption: TSelectboxOption | null;
  deviceFileSelectedDriverOption: TSelectboxOption | null;
  deviceFiles: FileRetrievalListingPayload;

  // White Labeling
  whiteLabeling: WhiteLabelParam;

  // crash track
  crashTrack: CrashTrackListingPayload;
};

export type TMapView = {
  center: TLatLng | null;
  zoom: number;
  mapType: TMapType | null;
};

export type TUserMapActivity = {
  assetMap: TMapView;
  fleetMap: TMapView;
};

export type TUserMapSelection = {
  assetMap: {
    selectedTrip: number | null;
    selectedTripPoint: VehicleMapDataPoint | null;
  };
  fleetMap: {
    selectedAssetItem: string | null;
  };
};

export type TVehicleLiveCamerasData = {
  imei: string;
  cameras: number[];
  organizationId: string;
  authToken: string;
};

export type TMobileHelpers = {
  viewportIsMobile: boolean;
  showLeftMenuDrawer: boolean;
  showRightMenuDrawer: boolean;
};

export type TUserAuthType = "pyDRF" | "azureAD";

export type TAppCommonState = {
  appOnline?: boolean;
  preLoginUserData?: TPreLoginUserData;
  user?: LegacyVerifyEmailOtpResponseSuccess;
  accessToken?: string;
  userAuthInProgress?: boolean;
  userAuthType?: TUserAuthType;
  userIsImpersonated?: boolean;
  account?: LegacyOrganizationAccount;
  timezoneForDateFns?: string;
  userCurrPos?: TLatLng;
  mapState?: TMapState;
  mapZoomForControls?: number;
  mobileHelpers?: TMobileHelpers;
  modals: TModalsState;
  listingQueryParams: TListingQueryParams;
  reportsByType: {
    [key: string]: TReport[] | TReportStatus;
  };
  reportVisibleColumns: {
    [key: string]: string[];
  };
  userMapActivity: TUserMapActivity;
  userMapRefreshing: boolean;
  userMapSelection?: TUserMapSelection;
  vehicleLiveCamerasData: TVehicleLiveCamerasData;
  isSelectedGroupModified: boolean;
  singleVMCameraCurrentConfigs: {
    [key: string]: CameraDeviceConfigProfile;
  };
  reportPageSizes: {
    [key: string]: number; // key:reportType,value:pageSize
  };
};

const initialState: TAppCommonState = {
  reportPageSizes: {},
  appOnline: navigator.onLine,
  preLoginUserData: undefined,
  user: undefined,
  accessToken: undefined,
  userAuthInProgress: false,
  userAuthType: "pyDRF",
  account: undefined,
  timezoneForDateFns: undefined,
  userCurrPos: undefined,
  mapState: undefined,
  mapZoomForControls: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
  mobileHelpers: {
    viewportIsMobile: false,
    showLeftMenuDrawer: false,
    showRightMenuDrawer: false,
  },
  modals: {
    showBuzzerAlertSingle: true,
    showEditColumnsDashboard: false,
    showBuzzerAlertList: false,
    buzzerAlerts: [],
    buzzerAlertsViewed: [],
    buzzerAlertsInProgress: [],

    showCreateUser: false,
    showCreateRole: false,
    showCloneRole: false,
    showEditColumns: false,
    showAdminsDropdown: false,
    showCreateVehicle: false,
    showCreateDriver: false,
    showCreateCustomIcon: false,
    showCreateGroup: false,
    createGroupUnder: "",
    showConfirmMoveGroup: false,
    showEditGroup: false,
    editGroupId: "",
    editGroupNewMode: false,
    showHierarchyGroupSelector: false,
    showReadOnlyViewForHierarchyGroup: null,

    showCreateFleetTag: false,
    showCreateGeozone: false,
    showCreateGeozoneCategory: false,
    showDeleteConfirmation: false,
    showDeleteImageConfirmation: false,
    showResetConfirmation: false,
    showMapFilter: false,
    showGroupSelector: false,
    showVehicleDetails: false,
    showVehicleLiveCameras: false,
    showVehicleFilter: false,
    showEventFilter: false,
    showLayerFilter: false,
    showWeatherForecast: false,
    showDuplicateReportModal: false,
    showInternalShareReportModal: false,
    showShareViaEmailReportModal: false,
    showAddEditReportModal: false,
    showSendText: false,
    showSendLocation: false,
    showReportOptions: false,
    showCustomReportOptions: false,
    showUpdateReportOptions: false,
    showReportTypeSelector: false,
    showVehicleCameraVideo: false,
    showCreateRule: false,
    isExpandedSideMenu: true,
    showAddMaintenanceDetails: false,
    showNotifications: false,
    showAlertNotifications: false,
    showNotificationPrefs: false,
    maintBucket: "",
    maintName: "",
    showUploadDocumentModal: false,
    showEditDocumentModal: false,
    showAddUsersToRoleModal: false,
    showCreateGroupChatModal: false,
    showCreateGroupSMSModal: false,
    showCreateDashboard: false,
    editDashboardId: null,
    showDashboardSelectorDropdown: false,
    showDashboardAddWidget: false,
    showGlobalSearchDropdown: false,

    showSysadminEnterprisesCreateNew: false,

    showPrimaryOverlay: false,

    showSwitchChartType: false,
    switchChartTypeForWidget: null,
    switchChartTypeForCustomWidgetId: null,

    widgetSelectedForPrint: null,

    showModalNewCustomReportStep1: false,
    showModalNewCustomReportStep2: false,
    showModalNewCustomReportStep3: false,
    showAdvanceFilterPopup: false,
    showModalMakeCustomReport: false,
    showModalMakeCopy: false,

    showUseCustomReport: false,
    customWidgetReport: null,
    customWidgetToEdit: null,
    showModalAssetDecodeVIN: false,
    showModalSelectDelegatedAccessUser: false,
    showModalMaintenanceFaultCodes: false,

    // cameras admin - visionmax
    showModalLinkAsset: false,
    showModalVMCameraLiveviewSN: null,
    showModalVMCameraLiveviewContinueSN: null,
    showModalVMCameraVoicecallSN: null,
    showModalVMCameraVoicecallIncoming: null,
    showParkingPhotoView: false,
    showModalFormatSDCard: false,
    showModalVMICChistory: false,
    showModalDiagnoseCamera: false,
    showModalWakeupCamera: null,
    showGroupCameraSelector: false,
    showSnapShotsView: false,
    showEventDownloadConfirmation: false,
    showEventComments: false,
    showPreviousSaveConfirmation: false,
    showRestoreDefaultConfirmation: false,
    showModalDeleteFile: false,
    showRequestVideo: {
      show: false,
      timestamp: 0,
    },
    showModalVideoPlayback: null,

    showContextMenu: false,
    contextMenuOptions: {
      Content: null,
      relativePosition: "top-left",
      widthClass: "p-2 w-40",
    },
    // White labeling
    showReferenceLandingModal: false,
    showDeleteDomainModal: false,
  },
  listingQueryParams: {
    users: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
      isactive: 1,
    },
    roles: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    vehicles: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
      isactive: 1,
    },
    drivers: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
      isactive: 1,
    },
    groups: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    fleetTags: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    geoZones: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : 0,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    geozoneCategories: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : 0,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    vehicleAlerts: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    vehicleAlertsSingle: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    buzzerAlerts: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    reports: {
      id: "",
      fields: [],
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    incidentsReview:{
      start_time: null,
      end_time: null,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    rules: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
      isactive: 1,
    },
    maintenance: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    documents: {
      accountid: ownerOrganization
        ? JSON.parse(ownerOrganization).accountid
        : "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    roleCategoryACL: {
      page: 1,
      page_size: APP_CONFIG.LISTINGS.TEMP_LARGE_PAGE_SIZE,
      role: "",
      category: 0,
    },
    globalSearch: {
      search: "",
    },

    sysadminEnterprises: {
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },

    accountUsers: {
      accountid: "",
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },

    deviceEvents: {
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      page: 1,
      serial_number: "",
      statuscodes: "",
      statuscodeslabel: "",
      start_time: null, // null helps identify initialization
      end_time: null, // null helps identify initialization
      duration: "yesterday",
    },
    deviceEventsDashboard: {
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      page: 1,
      serial_number: "",
      statuscodes: "",
      statuscodeslabel: "",
      start_time: null, // null helps identify initialization
      end_time: null, // null helps identify initialization
      duration: "yesterday",
    },
    deviceEventsSelectedCameraOption: null,
    deviceFileSelectedCameraOption: null,
    deviceFileSelectedDriverOption: null,
    deviceFiles: {
      keyword: "",
      offset: 0,
      limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      page: 1,
      serial_number: "",
      since: 0,
      until: 0,
      task_state_filter: "",
    },
    whiteLabeling: {
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
    },
    crashTrack: {
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      ...(window.location.hostname.includes("localhost")
        ? { search: "a3737", start_time: 1713769200, end_time: 1713855599 }
        : {
            search: "",
            start_time: null,
            end_time: null,
          }),
    },
  },
  reportsByType: Object.keys(REPORT_TYPES)
    .filter((item) => !isNaN(Number(item))) // keeps numeric keys, removes string keys like "ASSET_DETAIL", "DEVICE_GROUP_DETAIL", etc.
    .reduce(
      (acc, key) => {
        acc[key as REPORT_TYPES] = [] as TReport[];
        return acc;
      },
      {} as TAppCommonState["reportsByType"]
    ),
  reportVisibleColumns: {},
  userMapActivity: {
    assetMap: {
      center: null,
      zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
      mapType: null,
    },
    fleetMap: {
      center: null,
      zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
      mapType: null,
    },
  },
  userMapRefreshing: false,
  userMapSelection: {
    assetMap: {
      selectedTrip: null,
      selectedTripPoint: null,
    },
    fleetMap: {
      selectedAssetItem: null,
    },
  },
  vehicleLiveCamerasData: {
    imei: "",
    cameras: [],
    organizationId: "",
    authToken: "",
  },
  userIsImpersonated: false,
  isSelectedGroupModified: false,
  singleVMCameraCurrentConfigs: {},
};

export const commonSlice = createSlice({
  name: "commonSlice",
  initialState,
  reducers: {
    setReportPageSize: (
      state,
      action: PayloadAction<{ reportType: string; pageSize: number }>
    ) => {
      state.reportPageSizes[action.payload.reportType] =
        action.payload.pageSize;
    },
    setAppOnline: (state, action: PayloadAction<boolean>) => {
      state.appOnline = action?.payload;
    },
    setPreLoginUserData: (state, action: PayloadAction<TPreLoginUserData>) => {
      if (action?.payload) {
        state.preLoginUserData = {
          ...(state.preLoginUserData || {}),
          ...action?.payload,
        };
      }
    },
    setUserData: (
      state,
      action: PayloadAction<LegacyVerifyEmailOtpResponseSuccess>
    ) => {
      state.user = action?.payload;
      state.accessToken = action?.payload?.access_token;
    },
    setAccessToken: (state, action: PayloadAction<string>) => {
      state.accessToken = action?.payload;
    },
    setUserAuthInProgress: (state, action: PayloadAction<boolean>) => {
      state.userAuthInProgress = action?.payload;
    },
    setUserAuthType: (state, action: PayloadAction<TUserAuthType>) => {
      state.userAuthType = action?.payload;
    },
    setUserIsImpersonated: (state, action: PayloadAction<boolean>) => {
      state.userIsImpersonated = action?.payload;
    },
    setAccountData: (
      state,
      action: PayloadAction<LegacyOrganizationAccount>
    ) => {
      state.account = action?.payload;
    },
    setTimezoneForDateFns: (state, action: PayloadAction<string>) => {
      state.timezoneForDateFns = action?.payload;
    },
    setUserCurrPosData: (state, action: PayloadAction<TLatLng>) => {
      state.userCurrPos = action?.payload;
    },
    setMapStateData: (state, action: PayloadAction<Partial<TMapState>>) => {
      state.mapState = { ...state.mapState, ...action?.payload } as any;
    },
    setMapZoomForControlsData: (state, action: PayloadAction<number>) => {
      state.mapZoomForControls = action?.payload;
    },
    setMobileHelpersData: (state, action: PayloadAction<TMobileHelpers>) => {
      state.mobileHelpers = { ...(state.modals || {}), ...action?.payload };
    },
    setModalsData: (state, action: PayloadAction<TModalsState>) => {
      state.modals = { ...(state.modals || {}), ...action?.payload };
    },
    setModalsDataBuzzerAlertsViewed: (
      state,
      action: PayloadAction<TViewedBuzzerAlert[]>
    ) => {
      state.modals.buzzerAlertsViewed = action?.payload;
    },
    setModalsDataBuzzerAlertsInProgress: (
      state,
      action: PayloadAction<TViewedBuzzerAlert[]>
    ) => {
      state.modals.buzzerAlertsInProgress = action?.payload;
    },
    setListingQueryParams: (
      state,
      action: PayloadAction<TListingQueryParams>
    ) => {
      state.listingQueryParams = {
        ...(state.listingQueryParams || {}),
        ...action?.payload,
      };
    },
    setReportsByType: (
      state,
      action: PayloadAction<{
        type: REPORT_TYPES;
        reports: TReport[] | TReportStatus;
      }>
    ) => {
      state.reportsByType[action.payload.type] = action.payload.reports;
    },
    setReportVisibleColumns: (
      state,
      action: PayloadAction<{ type: string; columns: string[] }>
    ) => {
      state.reportVisibleColumns = {
        ...(state.reportVisibleColumns || {}),
        [action.payload.type]: action.payload.columns,
      };
    },
    setUserMapActivity: (state, action: PayloadAction<TUserMapActivity>) => {
      state.userMapActivity = {
        ...(state.userMapActivity || {}),
        ...action.payload,
      };
    },
    setUserMapRefreshing: (state, action: PayloadAction<boolean>) => {
      state.userMapRefreshing = action.payload;
    },
    setUserMapSelection: (state, action: PayloadAction<TUserMapSelection>) => {
      state.userMapSelection = {
        ...(state.userMapSelection || {}),
        ...action.payload,
      };
    },
    setVehicleLiveCamerasData: (
      state,
      action: PayloadAction<{
        imei: string;
        cameras: number[];
        organizationId: string;
        authToken: string;
      }>
    ) => {
      state.vehicleLiveCamerasData = action.payload;
    },
    setSelectedGroupIsModified: (state, action: PayloadAction<boolean>) => {
      state.isSelectedGroupModified = action.payload;
    },
    setSingleVMCameraCurrentConfigs: (
      state,
      action: PayloadAction<{ cid: string; profile: CameraDeviceConfigProfile }>
    ) => {
      state.singleVMCameraCurrentConfigs = {
        ...(state.singleVMCameraCurrentConfigs || {}),
        [action.payload.cid]: action.payload.profile,
      };
    },
  },
});

export const {
  setAppOnline,
  setPreLoginUserData,
  setUserData,
  setAccessToken,
  setUserAuthInProgress,
  setUserAuthType,
  setUserIsImpersonated,
  setAccountData,
  setTimezoneForDateFns,
  setUserCurrPosData,
  setMapStateData,
  setMapZoomForControlsData,
  setMobileHelpersData,
  setModalsData,
  setModalsDataBuzzerAlertsViewed,
  setModalsDataBuzzerAlertsInProgress,
  setListingQueryParams,
  setReportsByType,
  setReportVisibleColumns,
  setUserMapActivity,
  setUserMapRefreshing,
  setUserMapSelection,
  setVehicleLiveCamerasData,
  setSelectedGroupIsModified,
  setSingleVMCameraCurrentConfigs,
  setReportPageSize,
} = commonSlice.actions;

export default commonSlice.reducer;
